import { memo, useEffect, useRef, useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import { Area, AreaChart, ResponsiveContainer, Tooltip as RTooltip, YAxis } from "recharts";
import { InfoTip } from "./ChartCard";
import { ACCENTS, CARD_SX, COLORS, LIFT, MOTION_MS, TYPE, deltaTone, fmtDay, reducedMotion } from "./emsTheme";

/** Counts a number up to its value (300ms); shows it straight away under reduced motion. */
const useCountUp = (target) => {
    const [shown, setShown] = useState(target);
    const from = useRef(0);
    useEffect(() => {
        if (typeof target !== "number" || reducedMotion()) { setShown(target); return undefined; }
        const start = performance.now();
        const begin = from.current;
        let raf;
        const step = (t) => {
            const k = Math.min(1, (t - start) / MOTION_MS);
            setShown(begin + (target - begin) * k);
            if (k < 1) raf = requestAnimationFrame(step);
            else from.current = target;
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target]);
    return shown;
};

/**
 * The tile's trend line, kept out of the tile's own re-renders: the count-up re-renders the tile
 * every frame, and handing the chart new props mid-animation left the line drawn only part-way.
 */
const Sparkline = memo(({ data, color, gradId, label }) => {
    const last = data.length - 1;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <YAxis hide domain={[0, "auto"]} />
                <RTooltip cursor={false} formatter={(v) => [v, label]} labelFormatter={(_, p) => (p && p[0] ? fmtDay(p[0].payload.date) : "")}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#${gradId})`}
                    isAnimationActive={!reducedMotion()} animationDuration={600}
                    dot={(p) => (p.index === last ? <circle key="end" cx={p.cx} cy={p.cy} r={3.5} fill="#fff" stroke={color} strokeWidth={2} /> : <g key={p.index} />)} />
            </AreaChart>
        </ResponsiveContainer>
    );
});

/**
 * KPI tile. value is a number (formatted by `format`) or a ready string; delta is vs the previous
 * period of the same length, coloured by polarity with an arrow and a word, never colour alone.
 * accent: a key of ACCENTS, used for the top bar, the icon tile and the chart. The picture is
 * either `spark` ([{ date, value }], drawn as a small area chart) or a ready `visual` node.
 * Clicking the tile opens the matching ticket list.
 */
const KpiTile = ({ label, value, format = (v) => v, delta, deltaText, polarity, target, caption, info, spark, sparkLabel, visual,
    loading, onClick, secondary, accent = "purple", icon: Icon, sx }) => {
    const animated = useCountUp(typeof value === "number" ? value : null);
    const tone = deltaTone(polarity, delta);
    const Arrow = delta > 0 ? ArrowUpwardIcon : delta < 0 ? ArrowDownwardIcon : RemoveIcon;
    const clickable = !!onClick;
    const a = ACCENTS[accent] || ACCENTS.purple;
    const hasSpark = spark && spark.some((p) => p.value);
    const gradId = `kpi-${accent}-${label.replace(/\W/g, "")}`;

    const shell = { ...CARD_SX, p: 2.5, pt: 3, position: "relative", overflow: "hidden", minWidth: 0,
        "&::before": { content: '""', position: "absolute", left: 0, right: 0, top: 0, height: 4, background: a.grad }, ...sx };

    if (loading) {
        return <Box sx={{ ...shell, minHeight: 196 }}><Skeleton width="60%" /><Skeleton height={48} width="50%" /><Skeleton /><Skeleton height={44} /></Box>;
    }
    return (
        <Box role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined} onClick={onClick}
            onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
            aria-label={clickable ? `${label}: ${typeof value === "number" ? format(value) : value}. Open these tickets` : undefined}
            sx={{ ...shell, textAlign: "left", font: "inherit", display: "flex", flexDirection: "column", gap: 1,
                cursor: clickable ? "pointer" : "default", ...(clickable ? LIFT : {}),
                "&:focus-visible": { outline: `2px solid ${a.solid}`, outlineOffset: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {Icon && (
                    <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: a.grad, display: "grid", placeItems: "center", flexShrink: 0,
                        boxShadow: `0 4px 10px ${a.solid}33` }} aria-hidden>
                        <Icon sx={{ fontSize: 18, color: "#fff" }} />
                    </Box>
                )}
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.ink2, flex: 1, minWidth: 0 }}>{label}</Typography>
                {info && <Box component="span" onClick={(e) => e.stopPropagation()}><InfoTip text={info} label={`About ${label}`} /></Box>}
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ ...TYPE.kpi, fontWeight: 700, color: COLORS.ink, fontVariantNumeric: "tabular-nums" }}>
                    {typeof value === "number" ? format(animated ?? value) : (value ?? "No data")}
                </Typography>
                {secondary && <Typography sx={{ fontSize: 13, color: COLORS.ink3 }}>{secondary}</Typography>}
            </Box>
            {deltaText && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, px: 0.75, py: 0.125, borderRadius: "6px", bgcolor: `${tone.color}14` }}>
                        <Arrow sx={{ fontSize: 14, color: tone.color }} aria-hidden />
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: tone.color }}>
                            {deltaText}{tone.word ? ` · ${tone.word}` : ""}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: COLORS.ink3 }}>vs previous period</Typography>
                </Box>
            )}
            {target && <Typography sx={{ fontSize: 12, color: COLORS.ink2 }}>Target: {target}</Typography>}
            {visual ? <Box sx={{ mt: "auto" }}>{visual}</Box> : hasSpark && (
                <Box sx={{ height: 44, mt: "auto", mx: -0.5 }} aria-hidden>
                    <Sparkline data={spark} color={a.solid} gradId={gradId} label={sparkLabel || label} />
                </Box>
            )}
            {caption && <Typography sx={{ fontSize: 12, color: COLORS.ink3, mt: visual || hasSpark ? 0 : "auto" }}>{caption}</Typography>}
        </Box>
    );
};

export default KpiTile;
